import React, { Component } from "react"
import { Link } from "react-router-dom"
import { Arwes } from "arwes"
import styled from "styled-components"
import { connect } from "react-redux"
import { signIn } from "../components/user/redux/reducers"
import SignInInternal from "../components/user/SignInInternal"
import LoginGoogle from "../components/user/LoginGoogle"
import TerminalDock from "./TerminalDock"

const ASSETS = `${process.env.PUBLIC_URL}/assets`
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",")

const focusWithoutScroll = element => {
  if (!element || typeof element.focus !== "function") return

  try {
    element.focus({ preventScroll: true })
  } catch (e) {
    element.focus()
  }
}

const trapDialogFocus = event => {
  if (event.key !== "Tab") return

  const dialog = event.currentTarget
  const focusable = Array.from(
    dialog.querySelectorAll(FOCUSABLE_SELECTOR)
  ).filter(element => element.getClientRects().length > 0)

  if (!focusable.length) {
    event.preventDefault()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (event.shiftKey && (active === first || !dialog.contains(active))) {
    event.preventDefault()
    focusWithoutScroll(last)
  } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
    event.preventDefault()
    focusWithoutScroll(first)
  }
}

const ChatDockBase = ({
  isOpen,
  isMobile,
  viewportHeight,
  viewportTop,
  ...props
}) => <div {...props} />
const ChatBubbleBase = ({ isUser, ...props }) => <div {...props} />
const DropBase = ({ left, height, duration, delay, ...props }) => (
  <span {...props} />
)

const getViewportState = () => {
  if (typeof window === "undefined") {
    return {
      isMobileViewport: false,
      viewportHeight: 800,
      viewportTop: 0
    }
  }

  const viewport = window.visualViewport
  return {
    isMobileViewport: window.innerWidth <= 900,
    viewportHeight: Math.round(
      viewport ? viewport.height : window.innerHeight
    ),
    viewportTop: Math.round(viewport ? viewport.offsetTop : 0)
  }
}

const MarginTop = styled.div`
  margin-top: 50px;
`

const PageContent = styled.main`
  position: relative;
`
const Right = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 30px;

  @media (max-width: 900px) {
    justify-content: center;
    margin-left: 64px;
    margin-right: 0;
  }
`

const HeroLayout = styled.div`
  display: flex;
  min-height: 80vh;
  gap: 30px;
  align-items: center;
  justify-content: flex-end;
  padding: 0 30px 30px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    box-sizing: border-box;
    gap: 16px;
    padding: 0 8px 24px;
    width: 100%;
  }
`

const GifDock = styled.div`
  width: min(430px, 42vw);
  cursor: pointer;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 363px;
    align-self: center;
  }
`

const GifFrame = styled.div`
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 231, 255, 0.75);
  box-shadow: 0 0 24px rgba(216, 19, 255, 0.45);
  background: rgba(0, 0, 0, 0.5);
`

const CyberGif = styled.img`
  display: block;
  width: 100%;
  min-height: 250px;
  object-fit: cover;
`

const StreetGif = styled(CyberGif)`
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 250ms ease;

  ${GifFrame}:hover & {
    opacity: 1;
  }
`

const Panel = styled.div`
  width: 400px;
  max-width: calc(100vw - 90px);

  @media (max-width: 900px) {
    align-self: center;
    width: 100%;
    max-width: 400px;
  }
`

/* ---- AUTH UPLINK panel (grid-replies-preview-enhancement, TURN 3A) ---- */

const AuthPanel = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  padding: 28px 24px 20px;
  background: rgba(2, 0, 26, 0.97);
  border: 1px solid rgba(252, 38, 250, 0.6);
  box-shadow: 0 0 24px rgba(252, 38, 250, 0.25);

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    border-color: rgba(252, 38, 250, 0.9);
    pointer-events: none;
  }
  &::before {
    top: -1px;
    left: -1px;
    border-top: 2px solid rgba(252, 38, 250, 0.9);
    border-left: 2px solid rgba(252, 38, 250, 0.9);
  }
  &::after {
    top: -1px;
    right: -1px;
    border-top: 2px solid rgba(252, 38, 250, 0.9);
    border-right: 2px solid rgba(252, 38, 250, 0.9);
  }

  @media (max-width: 900px) {
    min-height: auto;
  }
`

const AuthCornerBL = styled.div`
  position: absolute;
  bottom: -1px;
  left: -1px;
  width: 14px;
  height: 14px;
  border-bottom: 2px solid rgba(252, 38, 250, 0.9);
  border-left: 2px solid rgba(252, 38, 250, 0.9);
  pointer-events: none;
`

const AuthCornerBR = styled.div`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 14px;
  height: 14px;
  border-bottom: 2px solid rgba(252, 38, 250, 0.9);
  border-right: 2px solid rgba(252, 38, 250, 0.9);
  pointer-events: none;
`

const PanelTitle = styled.div`
  font-family: "Rajdhani", "VT323", monospace;
  font-weight: 700;
  font-size: 24px;
  letter-spacing: 2px;
  color: #fc26fa;
  text-shadow: 0 0 10px rgba(252, 38, 250, 0.5);
`

const PanelSub = styled.div`
  font-family: "VT323", monospace;
  font-size: 15px;
  color: #6d7da6;
  margin-top: 2px;
`

const DashDivider = styled.div`
  border-top: 1px dashed rgba(0, 231, 255, 0.25);
  margin: 18px 0;
`

const AltDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0;

  .line {
    flex: 1;
    border-top: 1px dashed rgba(109, 125, 166, 0.35);
  }
  .label {
    font-family: "VT323", monospace;
    font-size: 13px;
    letter-spacing: 1px;
    color: #6d7da6;
  }
`

const GhostRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`

const GhostBtn = styled.button`
  flex: 1;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(109, 125, 166, 0.4);
  color: #6d7da6;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 231, 255, 0.7);
    color: #00e7ff;
  }
`

const PanelSpacer = styled.span`
  flex: 1;
  min-height: 18px;
`

const CreateCta = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid rgba(252, 38, 250, 0.5);
  background: rgba(252, 38, 250, 0.07);
  padding: 12px 14px;
  text-decoration: none;
  cursor: pointer;

  .ask {
    font-family: "VT323", monospace;
    font-size: 13px;
    letter-spacing: 1px;
    color: #6d7da6;
  }
  .cta {
    font-family: "Rajdhani", "VT323", monospace;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 2px;
    color: #fc26fa;
  }

  &:hover {
    background: rgba(252, 38, 250, 0.14);
    box-shadow: 0 0 12px rgba(252, 38, 250, 0.3);
  }
`

const SysFooter = styled.div`
  font-family: "VT323", monospace;
  font-size: 12px;
  color: #3a4670;
  text-align: center;
  margin-top: 10px;
`

const EasterEggBackdrop = styled.button`
  display: none;
  appearance: none;
  padding: 0;
  border: 0;

  @media (max-width: 900px) {
    display: block;
    position: fixed;
    inset: 0 0 0 64px;
    z-index: 1000;
    background: rgba(3, 0, 33, 0.72);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    touch-action: none;
  }
`

const ChatDock = styled(ChatDockBase)`
  position: fixed;
  left: 78px;
  bottom: 28px;
  z-index: 1005;
  width: 320px;
  max-width: calc(100vw - 110px);
  transform: ${(props) =>
    props.isOpen ? "translateX(0)" : "translateX(-24px)"};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  transition: opacity 220ms ease, transform 220ms ease;

  @media (max-width: 900px) {
    top: calc(
      ${(props) => props.viewportTop || 0}px +
        max(8px, env(safe-area-inset-top))
    );
    right: max(8px, env(safe-area-inset-right));
    bottom: auto;
    left: calc(64px + max(8px, env(safe-area-inset-left)));
    width: auto;
    height: min(
      320px,
      ${(props) => Math.max((props.viewportHeight || 320) - 64, 120)}px
    );
    max-width: none;
    transform: ${(props) =>
      props.isOpen ? "translateY(0)" : "translateY(-16px)"};
  }
`

const ChatShell = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid rgba(0, 231, 255, 0.65);
  background: rgba(2, 4, 10, 0.97);
  box-shadow: 0 0 30px rgba(190, 38, 252, 0.35);
  overflow: hidden;

  @media (max-width: 900px) {
    height: 100%;
  }
`

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  min-height: 52px;
  padding: 4px 4px 4px 12px;
  font-family: "VT323", monospace;
  font-size: 28px;
  color: #00e7ff;
  border-bottom: 1px solid rgba(95, 61, 255, 0.7);
`

const ChatTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DockCloseButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  margin-left: auto;
  padding: 0;
  border: 1px solid rgba(255, 0, 28, 0.7);
  background: rgba(255, 0, 28, 0.08);
  color: #ff6073;
  font-family: "VT323", monospace;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;

  &:hover {
    color: #fff;
    background: rgba(255, 0, 28, 0.2);
  }

  &:focus {
    outline: 2px solid #00e7ff;
    outline-offset: -3px;
  }
`

const BotAvatar = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(0, 231, 255, 0.7);
`

const ChatMessages = styled.div`
  flex: 0 0 auto;
  height: 230px;
  overflow-y: auto;
  padding: 14px;
  overscroll-behavior: contain;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    padding: 12px;
  }
`

const ChatBubble = styled(ChatBubbleBase)`
  width: fit-content;
  max-width: 88%;
  margin: ${(props) => (props.isUser ? "0 0 10px auto" : "0 auto 10px 0")};
  padding: 8px 10px;
  color: ${(props) => (props.isUser ? "#02020a" : "#fff")};
  background: ${(props) =>
    props.isUser ? "rgba(0, 231, 255, 0.9)" : "rgba(255, 255, 255, 0.14)"};
  border: 1px solid
    ${(props) =>
      props.isUser ? "rgba(0, 231, 255, 1)" : "rgba(216, 19, 255, 0.5)"};
  font-family: "VT323", monospace;
  font-size: 23px;
`

const ChatForm = styled.form`
  display: flex;
  flex: 0 0 44px;
  min-height: 44px;
  border-top: 1px solid rgba(95, 61, 255, 0.7);
  background: #02040a;
`

const ChatInput = styled.input`
  min-width: 0;
  flex: 1;
  border: 0;
  outline: none;
  padding: 10px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-family: "VT323", monospace;
  font-size: 22px;
  border-radius: 0;
  appearance: none;
`

const SendButton = styled.button`
  border: 0;
  border-left: 1px solid rgba(0, 231, 255, 0.65);
  background: rgba(0, 231, 255, 0.16);
  color: #00e7ff;
  padding: 0 14px;
  font-family: "VT323", monospace;
  font-size: 22px;
  cursor: pointer;
  min-width: 64px;
  touch-action: manipulation;
`

const OfflineNotice = styled.div`
  margin: 0 0 14px;
  color: #00ff63;
  font-family: "VT323", monospace;
  font-size: 24px;
  animation: blink 1s linear infinite;

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`

const RainLayer = styled.div`
  pointer-events: none;
  position: fixed;
  inset: 0;
  overflow: hidden;
  z-index: 2;
`

const Drop = styled(DropBase)`
  position: absolute;
  top: -20vh;
  left: ${(props) => props.left}%;
  width: 1px;
  height: ${(props) => props.height}px;
  background: linear-gradient(transparent, rgba(0, 231, 255, 0.8));
  animation: cyber-rain ${(props) => props.duration}s linear infinite;
  animation-delay: ${(props) => props.delay}s;

  @keyframes cyber-rain {
    to {
      transform: translate3d(-30px, 125vh, 0);
    }
  }
`

class Home extends Component {
  state = {
    show: false,
    chatbot: false,
    terminalOpen: false,
    enableFirstTime: false,
    enableOffline: false,
    enableDirty: false,
    awaitingAfterRain: false,
    chatInput: "",
    numDrops: 24,
    messages: [
      {
        from: "bot",
        text: "Hi how can I help you?"
      }
    ],
    ...getViewportState()
  }

  chatCloseButton = React.createRef()

  componentDidMount() {
    this.showTimer = setTimeout(() => {
      this.setState({
        show: true
      })
    }, 300)

    this.visualViewport = window.visualViewport
    window.addEventListener("resize", this.queueViewportSync)
    window.addEventListener("orientationchange", this.queueViewportSync)
    document.addEventListener("keydown", this.handleGlobalKeyDown)

    if (this.visualViewport) {
      this.visualViewport.addEventListener("resize", this.queueViewportSync)
      this.visualViewport.addEventListener("scroll", this.queueViewportSync)
    }

    this.syncViewport()
  }

  componentWillUnmount() {
    clearTimeout(this.showTimer)
    if (this.viewportFrame) {
      window.cancelAnimationFrame(this.viewportFrame)
    }

    window.removeEventListener("resize", this.queueViewportSync)
    window.removeEventListener("orientationchange", this.queueViewportSync)
    document.removeEventListener("keydown", this.handleGlobalKeyDown)

    if (this.visualViewport) {
      this.visualViewport.removeEventListener(
        "resize",
        this.queueViewportSync
      )
      this.visualViewport.removeEventListener(
        "scroll",
        this.queueViewportSync
      )
    }
  }

  syncViewport = () => {
    const next = getViewportState()
    this.setState(prevState => {
      if (
        prevState.isMobileViewport === next.isMobileViewport &&
        prevState.viewportHeight === next.viewportHeight &&
        prevState.viewportTop === next.viewportTop
      ) {
        return null
      }
      return next
    })
  }

  queueViewportSync = () => {
    if (this.viewportFrame) {
      window.cancelAnimationFrame(this.viewportFrame)
    }
    this.viewportFrame = window.requestAnimationFrame(() => {
      this.viewportFrame = null
      this.syncViewport()
    })
  }

  blurActiveField = () => {
    if (typeof document === "undefined") return
    const active = document.activeElement
    if (active && typeof active.blur === "function") {
      active.blur()
    }
  }

  rememberEasterEggTrigger = () => {
    if (typeof document !== "undefined") {
      this.easterEggTrigger = document.activeElement
    }
  }

  restoreEasterEggTrigger = () => {
    const trigger = this.easterEggTrigger
    this.easterEggTrigger = null

    if (!trigger) return

    window.requestAnimationFrame(() => {
      if (trigger.isConnected) {
        focusWithoutScroll(trigger)
      }
    })
  }

  focusChatDialog = () => {
    if (!this.state.isMobileViewport) return

    window.requestAnimationFrame(() => {
      focusWithoutScroll(this.chatCloseButton.current)
    })
  }

  closeChat = () => {
    this.blurActiveField()
    this.setState({ chatbot: false }, this.restoreEasterEggTrigger)
  }

  closeTerminal = () => {
    this.blurActiveField()
    this.setState({ terminalOpen: false }, this.restoreEasterEggTrigger)
  }

  closeEasterEggs = () => {
    this.blurActiveField()
    this.setState(
      { chatbot: false, terminalOpen: false },
      this.restoreEasterEggTrigger
    )
  }

  handleGlobalKeyDown = event => {
    if (
      event.key === "Escape" &&
      (this.state.chatbot || this.state.terminalOpen)
    ) {
      event.preventDefault()
      this.closeEasterEggs()
    }
  }
  handleSubmit = (values) => {
    this.props.signIn(values)
  }

  helpBot = () => {
    const opening = !this.state.chatbot
    if (opening) {
      this.rememberEasterEggTrigger()
    }
    this.blurActiveField()
    this.setState(
      (prevState) => {
        const nextOpen = !prevState.chatbot

        return {
          chatbot: nextOpen,
          terminalOpen: nextOpen ? false : prevState.terminalOpen,
          enableFirstTime: true,
          enableOffline:
            prevState.enableDirty && nextOpen
              ? true
              : prevState.enableOffline,
        }
      },
      () => {
        if (opening) {
          this.focusChatDialog()
        } else {
          this.restoreEasterEggTrigger()
        }

        if (this.state.enableOffline) {
          setTimeout(() => {
            this.setState({
              enableOffline: false,
            })
          }, 3500)
        }
      }
    )
  }

  toggleTerminal = () => {
    const opening = !this.state.terminalOpen
    if (opening) {
      this.rememberEasterEggTrigger()
    }
    this.blurActiveField()
    this.setState(prevState => {
      const nextOpen = !prevState.terminalOpen
      return {
        terminalOpen: nextOpen,
        chatbot: nextOpen ? false : prevState.chatbot
      }
    }, () => {
      if (!opening) {
        this.restoreEasterEggTrigger()
      }
    })
  }

  changeNumOfDrops = () => {
    console.log("%c raining harder ", "background:black;color:DarkSlateGray;")
    this.setState({
      numDrops: 90,
    })
  }

  handleChatInput = (event) => {
    this.setState({
      chatInput: event.target.value,
    })
  }

  handleChatSubmit = (event) => {
    event.preventDefault()
    const value = this.state.chatInput.trim()

    if (!value) {
      return
    }

    this.setState(
      (prevState) => ({
        chatInput: "",
        messages: [
          ...prevState.messages,
          {
            from: "user",
            text: value,
          },
        ],
      }),
      () => {
        if (value.toLowerCase() === "rain" && !this.state.awaitingAfterRain) {
          this.changeNumOfDrops()
          setTimeout(() => {
            this.setState((prevState) => ({
              awaitingAfterRain: true,
              messages: [
                ...prevState.messages,
                {
                  from: "bot",
                  text: "it is raining cats and dogs",
                },
              ],
            }))
          }, 500)
          return
        }

        this.endChat()
      }
    )
  }

  endChat = () => {
    setTimeout(() => {
      this.setState((prevState) => ({
        enableOffline: true,
        enableDirty: true,
        awaitingAfterRain: false,
        messages: [
          ...prevState.messages,
          {
            from: "bot",
            text: "Im sorry, due to the rain, all our servers are offline.",
          },
        ],
      }))
    }, 500)

    setTimeout(() => {
      this.setState({
        enableOffline: false,
      })
    }, 12500)
  }

  renderRain() {
    const drops = Array.from({ length: this.state.numDrops })

    return (
      <RainLayer>
        {drops.map((_, index) => (
          <Drop
            key={index}
            left={(index * 37) % 100}
            height={18 + (index % 5) * 8}
            duration={0.85 + (index % 6) * 0.12}
            delay={(index % 13) * -0.18}
          />
        ))}
      </RainLayer>
    )
  }

  renderChat() {
    const isOpen = this.state.chatbot && this.state.enableFirstTime
    if (!isOpen) return null

    return (
      <ChatDock
        isOpen={isOpen}
        isMobile={this.state.isMobileViewport}
        viewportHeight={this.state.viewportHeight}
        viewportTop={this.state.viewportTop}
        role="dialog"
        aria-modal={this.state.isMobileViewport ? "true" : undefined}
        aria-labelledby="cyberchat-title"
        onKeyDown={trapDialogFocus}
      >
        <ChatShell>
          <ChatHeader>
            <BotAvatar src={`${ASSETS}/smallpilot.png`} alt="" />
            <ChatTitle id="cyberchat-title">CyberChat</ChatTitle>
            <DockCloseButton
              ref={this.chatCloseButton}
              type="button"
              onClick={this.closeChat}
              aria-label="Close CyberChat"
              title="Close CyberChat"
            >
              ×
            </DockCloseButton>
          </ChatHeader>
          <ChatMessages>
            {this.state.messages.map((message, index) => (
              <ChatBubble
                key={`${message.from}-${index}`}
                isUser={message.from === "user"}
              >
                {message.text}
              </ChatBubble>
            ))}
          </ChatMessages>
          <ChatForm onSubmit={this.handleChatSubmit}>
            <ChatInput
              value={this.state.chatInput}
              onChange={this.handleChatInput}
              aria-label="CyberChat message"
              placeholder="Type rain..."
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterkeyhint="send"
            />
            <SendButton type="submit">Send</SendButton>
          </ChatForm>
        </ChatShell>
      </ChatDock>
    )
  }

  render() {
    const easterEggOpen = this.state.chatbot || this.state.terminalOpen
    const mobileModalOpen = this.state.isMobileViewport && easterEggOpen

    return (
      <Arwes
        animate
        background={`${ASSETS}/image.png`}
        pattern={`${ASSETS}/window.png`}
      >
        {this.renderRain()}
        {this.state.isMobileViewport && easterEggOpen && (
          <EasterEggBackdrop
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={this.closeEasterEggs}
          />
        )}
        {this.renderChat()}
        <PageContent
          aria-hidden={mobileModalOpen ? "true" : undefined}
          inert={mobileModalOpen ? "" : undefined}
        >
          <MarginTop />
          <Right>
            <HeroLayout>
              <GifDock>
                <GifFrame>
                  <CyberGif
                    src={`${ASSETS}/cyberpunk.gif`}
                    alt="Cyberpunk city loop"
                  />
                  <StreetGif src={`${ASSETS}/street.gif`} alt="" />
                </GifFrame>
              </GifDock>

              <Panel>
                <AuthPanel>
                  <AuthCornerBL />
                  <AuthCornerBR />
                  <PanelTitle>CYBER COMMUNICATIONS</PanelTitle>
                  <PanelSub>// secure uplink to the grid network</PanelSub>

                  <DashDivider />

                  <SignInInternal onSubmit={this.handleSubmit} />

                  <AltDivider>
                    <span className="line" />
                    <span className="label">ALTERNATE UPLINK</span>
                    <span className="line" />
                  </AltDivider>

                  <LoginGoogle />

                  <GhostRow>
                    <GhostBtn type="button" onClick={this.helpBot}>
                      ? HELP
                    </GhostBtn>
                    <GhostBtn type="button" onClick={this.toggleTerminal}>
                      &gt;_ TERMINAL
                    </GhostBtn>
                  </GhostRow>

                  <PanelSpacer />

                  {this.state.enableOffline && (
                    <OfflineNotice>Servers offline</OfflineNotice>
                  )}

                  <CreateCta to="/signUp">
                    <span className="ask">NEW ON THE GRID?</span>
                    <span className="cta">CREATE YOUR HANDLE ▸</span>
                  </CreateCta>
                  <SysFooter>SYS.AUTH v2.1 // UPLINK ENCRYPTED</SysFooter>
                </AuthPanel>
              </Panel>
            </HeroLayout>
          </Right>
        </PageContent>
        <TerminalDock
          isExpanded={this.props.isExpanded}
          isOpen={this.state.terminalOpen}
          isMobile={this.state.isMobileViewport}
          viewportHeight={this.state.viewportHeight}
          viewportTop={this.state.viewportTop}
          onClose={this.closeTerminal}
        />
      </Arwes>
    )
  }
}

export default connect(null, { signIn })(Home)
